import { useCallback, useMemo, useState } from 'react'

export interface PaginationInfo {
  startCursor?: string
  endCursor?: string
  hasNextPage: boolean
  hasPreviousPage: boolean
}

export interface UsePaginationProps {
  pageSize?: number
}

export const usePagination = ({ pageSize = 10 }: UsePaginationProps = {}) => {
  const [afterCursor, setAfterCursor] = useState<string>()
  const [beforeCursor, setBeforeCursor] = useState<string>()

  // Memoize variables to avoid creating new object references on every render
  const variables = useMemo(
    () => ({
      afterCursor,
      beforeCursor,
      first: pageSize,
    }),
    [afterCursor, beforeCursor, pageSize]
  )

  const goToNextPage = useCallback((pageInfo: PaginationInfo) => {
    if (pageInfo.hasNextPage) {
      setAfterCursor(pageInfo.endCursor)
      setBeforeCursor(undefined)
    }
  }, [])

  const goToPreviousPage = useCallback((pageInfo: PaginationInfo) => {
    if (pageInfo.hasPreviousPage) {
      setBeforeCursor(pageInfo.startCursor)
      setAfterCursor(undefined)
    }
  }, [])

  const resetPagination = useCallback(() => {
    setAfterCursor(undefined)
    setBeforeCursor(undefined)
  }, [])

  return {
    variables,
    goToNextPage,
    goToPreviousPage,
    resetPagination,
    afterCursor,
    beforeCursor,
  }
}
